/**
 * Formulaire Shopify 8 étapes (06_Specification_formulaire_Shopify.md) —
 * définition des champs, validation par étape, et sérialisation vers les
 * propriétés Shopify EXACTES de schemas/shopify_mapping.json (le même
 * contrat que services/ingestion/mapping.js consomme côté webhook — les
 * deux doivent rester des fonctions inverses l'une de l'autre, voir
 * form_state.test.js#round-trip).
 *
 * Double environnement (Node pour les tests, navigateur pour le vrai
 * formulaire) — pas de dépendance à un bundler.
 *
 * Découpage en 8 étapes : reconstruction proposée à partir des champs
 * listés en 06 (le détail exact des 8 étapes du CDC V11 §6.2 n'était pas
 * dans les documents disponibles) — **confirmé par la fondatrice le
 * 2026-09-08** (voir ADR-021, 17_Registre_hypotheses_decisions_ADR.md).
 */
(function (root, factory) {
  if (typeof module === "object" && module.exports) {
    module.exports = factory();
  } else {
    root.NarratiaFormState = factory();
  }
})(typeof window !== "undefined" ? window : this, function () {
  "use strict";

  const MAX_CHARACTERS = 5; // 03 §3 — même contrainte que mapping.js
  const MAX_QUANTITY = 10; // même plafond que services/gelato/order_builder.py#MAX_SANE_QUANTITY —
  // toujours 1 pour story_digital (voir validateStep ci-dessous), un
  // client qui veut plusieurs exemplaires imprimés identiques n'a besoin
  // que de la quantité native du panier Shopify, pas d'un champ dédié.

  const CLIENT_TYPES = ["particulier", "famille", "couple", "groupe", "entreprise"];
  const PRODUCTS = ["story_digital", "story_signature"];

  // Liste provisoire — aucune liste de langues/pays supportés n'est
  // fixée dans les documents disponibles (06 demande juste un champ
  // "langue" et un "pays de livraison"). Codes ISO (639-1 / 3166-1
  // alpha-2) pour rester compatibles avec les conventions Shopify
  // (voir shipping_address.country_code dans le pipeline audité, 01).
  const LANGUAGES = [
    { code: "fr", label: "Français" },
    { code: "en", label: "Anglais" },
    { code: "es", label: "Espagnol" },
    { code: "de", label: "Allemand" },
    { code: "it", label: "Italien" },
    { code: "nl", label: "Néerlandais" },
    { code: "pt", label: "Portugais" },
    { code: "ar", label: "Arabe" },
  ];

  // Recoupée avec la liste officielle Gelato le 2026-09-08 (ADR-022,
  // schemas/gelato_delivery_countries.json — 223 pays livrés par Gelato,
  // source support.gelato.com du 30/10/2025). Les 17 pays ci-dessous sont
  // tous confirmés livrables ; périmètre volontairement restreint pour le
  // lancement (validé par la fondatrice), pas une limite technique de
  // Gelato — voir test de non-régression dans form_state.test.js.
  const COUNTRIES = [
    { code: "FR", label: "France" },
    { code: "BE", label: "Belgique" },
    { code: "CH", label: "Suisse" },
    { code: "LU", label: "Luxembourg" },
    { code: "DE", label: "Allemagne" },
    { code: "ES", label: "Espagne" },
    { code: "IT", label: "Italie" },
    { code: "NL", label: "Pays-Bas" },
    { code: "PT", label: "Portugal" },
    { code: "AT", label: "Autriche" },
    { code: "IE", label: "Irlande" },
    { code: "GB", label: "Royaume-Uni" },
    { code: "CA", label: "Canada" },
    { code: "US", label: "États-Unis" },
    { code: "MA", label: "Maroc" },
    { code: "TN", label: "Tunisie" },
    { code: "DZ", label: "Algérie" },
  ];

  const STEPS = [
    { id: "profil", title: "Qui commande ?" },
    { id: "produit", title: "Le produit" },
    { id: "personnages", title: "Vos personnages" },
    { id: "histoire", title: "L'histoire" },
    { id: "consentement", title: "Consentement" },
    { id: "style", title: "Style (optionnel)" },
    { id: "livraison", title: "Livraison" },
    { id: "recap", title: "Récapitulatif" },
  ];

  function emptyCharacter() {
    // `photos` : liste d'URLs déjà uploadées (form-preview.html / dev
    // harness, contrat historique). `photo_count` : nombre de fichiers
    // réellement sélectionnés dans les <input type="file"> natifs du vrai
    // thème Shopify (narratia-order-form.js) — les fichiers eux-mêmes ne
    // sont jamais visibles en JS (Shopify les héberge directement), donc
    // ce compteur est la seule représentation possible côté état. Les deux
    // champs sont mutuellement exclusifs selon l'environnement ; voir
    // photoCount() ci-dessous pour la règle qui les unifie.
    return { name: "", age_range: "", role: "", pronouns: "", photos: [], photo_count: 0, is_minor: false };
  }

  // Nombre de photos effectivement fournies par un personnage, quel que
  // soit l'environnement (preview avec URLs, ou vrai thème avec fichiers
  // natifs comptés à part — voir emptyCharacter()). Ne JAMAIS lire
  // `c.photos.length` seul pour valider une étape : dans le vrai thème,
  // `c.photos` reste toujours vide (bug corrigé, voir CLAUDE.md/audit
  // 2026-09-09 "photo_selected_but_step_valid").
  function photoCount(c) {
    if (c && c.photos && c.photos.length) return c.photos.length;
    return (c && c.photo_count) || 0;
  }

  function emptyFormState() {
    return {
      client_type: "",
      occasion_type: "",
      product: "",
      // ADR-092 (17/09) — choix client de style visuel du rendu final
      // (schemas/order_form.schema.json#style_choice). "bd_manga" = défaut
      // historique, "realiste" = nouveau choix, non encore validé
      // visuellement (voir services/photo/prompts.py#STYLE_FAMILIES).
      style_choice: "bd_manga",
      quantity: 1,
      language: "fr",
      delivery_country: "",
      characters: [emptyCharacter()],
      context: "",
      tone: "",
      important_places: [],
      memories: [],
      message_to_convey: "",
      avoid_topics: [],
      consent: { photos_usage: false, minor_representation: false, third_party_representation: false, version: "" },
      delivery_email: "",
      shipping_address: null,
    };
  }

  function isNonEmptyString(v) {
    return typeof v === "string" && v.trim().length > 0;
  }

  // -----------------------------------------------------------------
  // Validation par étape — miroir côté client des contraintes de
  // schemas/07_Contrats_JSON_et_schemas/order_form.schema.json (ne PAS
  // dupliquer des règles différentes ; en cas de doute, le schéma JSON
  // côté serveur fait foi, ceci n'est qu'un confort UX).
  // -----------------------------------------------------------------

  function validateStep(stepId, state) {
    const errors = [];
    const err = (field, message) => errors.push({ field, message });

    switch (stepId) {
      case "profil":
        if (!CLIENT_TYPES.includes(state.client_type)) err("client_type", "Sélectionnez qui commande.");
        if (!isNonEmptyString(state.occasion_type)) err("occasion_type", "Précisez l'occasion.");
        break;

      case "produit":
        if (!PRODUCTS.includes(state.product)) err("product", "Choisissez Story Digital ou Story Signature.");
        if (!LANGUAGES.some((l) => l.code === state.language)) err("language", "Choisissez une langue dans la liste.");
        if (state.product === "story_signature") {
          if (!isNonEmptyString(state.delivery_country)) {
            err("delivery_country", "Pays de livraison requis pour l'imprimé.");
          } else if (!COUNTRIES.some((c) => c.code === state.delivery_country)) {
            err("delivery_country", "Pays de livraison non reconnu dans la liste.");
          }
          const qty = Number(state.quantity);
          if (!Number.isInteger(qty) || qty < 1 || qty > MAX_QUANTITY) {
            err("quantity", `Nombre d'exemplaires invalide (entre 1 et ${MAX_QUANTITY}).`);
          }
        }
        break;

      case "personnages": {
        const chars = state.characters || [];
        if (chars.length < 1) err("characters", "Ajoutez au moins un personnage.");
        if (chars.length > MAX_CHARACTERS) err("characters", `5 personnages maximum (${chars.length} fournis).`);
        chars.forEach((c, i) => {
          if (!isNonEmptyString(c.name)) err(`characters[${i}].name`, `Personnage ${i + 1} : prénom requis.`);
          if (!isNonEmptyString(c.role)) err(`characters[${i}].role`, `Personnage ${i + 1} : rôle requis.`);
          if (!isNonEmptyString(c.age_range)) err(`characters[${i}].age_range`, `Personnage ${i + 1} : tranche d'âge requise.`);
          const nPhotos = photoCount(c);
          if (nPhotos < 1) err(`characters[${i}].photos`, `Personnage ${i + 1} : au moins une photo requise.`);
          if (nPhotos > 5) err(`characters[${i}].photos`, `Personnage ${i + 1} : 5 photos maximum.`);
        });
        break;
      }

      case "histoire":
        if (!isNonEmptyString(state.context)) err("context", "Décrivez le contexte de l'histoire.");
        break;

      case "consentement": {
        if (!state.consent || state.consent.photos_usage !== true) {
          err("consent.photos_usage", "Le consentement à l'utilisation des photos est obligatoire.");
        }
        const hasMinor = (state.characters || []).some((c) => c.is_minor);
        if (hasMinor && (!state.consent || state.consent.minor_representation !== true)) {
          err("consent.minor_representation", "Consentement renforcé requis : au moins un personnage est mineur.");
        }
        if (!state.consent || !isNonEmptyString(state.consent.version)) {
          err("consent.version", "Version du texte de consentement manquante (bug interne, pas une erreur utilisateur).");
        }
        break;
      }

      case "style":
        break; // facultatif par construction (06 : "champs facultatifs/conditionnels")

      case "livraison":
        if (!isNonEmptyString(state.delivery_email) || state.delivery_email.indexOf("@") === -1) {
          err("delivery_email", "Adresse e-mail de livraison invalide.");
        }
        if (state.product === "story_signature" && !state.shipping_address) {
          err("shipping_address", "Adresse de livraison requise pour l'imprimé.");
        }
        break;

      case "recap":
        STEPS.filter((s) => s.id !== "recap").forEach((s) => {
          errors.push(...validateStep(s.id, state).errors);
        });
        break;

      default:
        throw new Error("Étape inconnue : " + stepId);
    }

    return { valid: errors.length === 0, errors };
  }

  function validateAll(state) {
    return validateStep("recap", state);
  }

  // -----------------------------------------------------------------
  // Sérialisation vers les propriétés Shopify — DOIT rester le miroir
  // exact de schemas/shopify_mapping.json (mêmes noms de propriétés, même
  // séparateur '|' pour les listes) pour que mapping.js#shopifyOrderToOrderForm
  // puisse ensuite reconstruire un order_form équivalent côté webhook.
  // -----------------------------------------------------------------

  function joinList(list) {
    return (list || []).filter(Boolean).join("|");
  }

  function ouiNon(bool) {
    return bool ? "oui" : "non";
  }

  const VARIANT_TITLE_BY_PRODUCT = {
    story_digital: "Version PDF",
    story_signature: "Version imprimée",
  };

  function resolveVariantTitle(product) {
    const title = VARIANT_TITLE_BY_PRODUCT[product];
    if (!title) throw new Error("Produit inconnu, impossible de résoudre le variant Shopify : " + product);
    return title;
  }

  function serializeToShopifyProperties(state) {
    const props = {
      client_type: state.client_type,
      occasion_type: state.occasion_type,
      language: state.language,
      // ADR-092 (17/09) — même raison que client_type/language ci-dessus :
      // le <select name="properties[style_choice]"> vit dans l'étape
      // "style", détruite (stepFields.innerHTML="") dès qu'on change
      // d'étape — sans ce miroir, la valeur choisie serait perdue à la
      // soumission finale (même classe de bug corrigée le 2026-09-09).
      style_choice: state.style_choice || "bd_manga",
      contexte: state.context,
      ton: state.tone,
      lieux_importants: joinList(state.important_places),
      souvenirs: joinList(state.memories),
      message_a_transmettre: state.message_to_convey,
      sujets_a_eviter: joinList(state.avoid_topics),
      consentement_photos: ouiNon(state.consent && state.consent.photos_usage),
      // Vraie propriété soumise (contrat v1.2.0) — avant ce correctif,
      // aucune propriété Shopify ne portait ce consentement : la case à
      // cocher restait purement locale (checkboxLocal côté thème),
      // jamais transmise, jamais conservée comme preuve.
      consentement_autorite_parentale: ouiNon(state.consent && state.consent.minor_representation),
      consentement_tiers: ouiNon(state.consent && state.consent.third_party_representation),
      consentement_version: (state.consent && state.consent.version) || "",
    };

    (state.characters || []).forEach((c, i) => {
      const n = i + 1;
      props[`personnage_${n}_nom`] = c.name;
      props[`personnage_${n}_age`] = c.age_range;
      props[`personnage_${n}_role`] = c.role;
      // Les pronoms ne sont plus demandés ni transmis par les nouvelles
      // commandes. Le champ reste accepté en lecture pour assurer la
      // rétrocompatibilité avec les anciennes commandes déjà enregistrées.
      // Un champ par photo (contrat v1.1.0, shopify_mapping.json) — dans
      // le vrai thème, chaque <input type="file" name="properties[...]">
      // porte directement ce nom ; ici on reproduit la même forme pour
      // que l'aperçu et le round-trip avec mapping.js restent fidèles.
      (c.photos || []).slice(0, 5).forEach((url, k) => {
        if (url) props[`personnage_${n}_photo_${k + 1}`] = url;
      });
      props[`personnage_${n}_mineur`] = ouiNon(c.is_minor);
    });

    // Propriétés vides retirées : Shopify n'a pas besoin de propriétés
    // vides, et ça évite de polluer les notifications de commande.
    Object.keys(props).forEach((k) => {
      if (props[k] === "" || props[k] === undefined) delete props[k];
    });

    return props;
  }

  // -----------------------------------------------------------------
  // Brouillon (06 : "sauvegarde de brouillon") — stockage local
  // uniquement, jamais envoyé nulle part avant validation + consentement
  // explicite à l'étape finale.
  // -----------------------------------------------------------------

  const DRAFT_STORAGE_KEY = "narratia_form_draft_v1";
  const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

  function saveDraft(state, storage) {
    storage = storage || (typeof localStorage !== "undefined" ? localStorage : null);
    if (!storage) return false;
    try {
      storage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({ saved_at: Date.now(), state }));
      return true;
    } catch (e) {
      return false; // stockage indisponible (navigation privée, quota...) — non bloquant
    }
  }

  function loadDraft(storage) {
    storage = storage || (typeof localStorage !== "undefined" ? localStorage : null);
    if (!storage) return null;
    try {
      const raw = storage.getItem(DRAFT_STORAGE_KEY);
      if (!raw) return null;
      const draft = JSON.parse(raw);
      if (!draft || typeof draft.saved_at !== "number" || !draft.state || Date.now() - draft.saved_at > DRAFT_TTL_MS) {
        storage.removeItem(DRAFT_STORAGE_KEY);
        return null;
      }
      return draft.state;
    } catch (e) {
      return null;
    }
  }

  function clearDraft(storage) {
    storage = storage || (typeof localStorage !== "undefined" ? localStorage : null);
    if (!storage) return;
    try {
      storage.removeItem(DRAFT_STORAGE_KEY);
    } catch (e) {
      /* non bloquant */
    }
  }

  return {
    MAX_CHARACTERS,
    MAX_QUANTITY,
    CLIENT_TYPES,
    PRODUCTS,
    LANGUAGES,
    COUNTRIES,
    STEPS,
    emptyCharacter,
    emptyFormState,
    photoCount,
    validateStep,
    validateAll,
    serializeToShopifyProperties,
    resolveVariantTitle,
    saveDraft,
    loadDraft,
    clearDraft,
  };
});
